import React, { useState } from 'react';
import { useEffect } from 'react';
import { FormControl, Typography, CircularProgress } from '@mui/material';
import { Card, CardContent } from "@mui/material";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import ComponentHeaderTitles from '../../../ComponentHeaderTitles';
const ParticulierVerbruikEstimaterStep1 = ({
    setIsFetchingData,
    chosenBuilding,
    setBuildingType,
    setOutput,
    onValidationChange,
    entiteit,
    houseHoldSize }) => {
    const BACKEND_URL = "https://offerte.api.cleanenergy.nl/index?m=estimate";
    // Set the state to previously chosen building type, or to 'appartement' (the first option) if no building type was chosen yet
    const [woning, setWoning] = useState(chosenBuilding ? chosenBuilding : 'appartement');
    const [fetchingData, setFetchingData] = useState(false);
    // In case the api does not return a valid response, we want to set the output to a default value
    // this value is for an 'appartement' with 1 person
    const defaultOutput = {
        e_single: 1800,
        e_high: 1260,
        e_low: 540,
        gas: 800
    };
    const validateStepTwo = (woning) => {
        if (woning === "") {
            return [false, 'Selecteer het type woning'];
        }
        return [true, null];
    };

    const entity_is_business = (entiteit) => {
        if (entiteit === "Particulier") {
            return 0;
        }
        return 1;
    }

    const get_persons = (houseHoldSize) => {
        // if houseHoldSize is 5 or 6, we return 5-6
        // if houseHoldSize is 3 or 4, we return 3-4
        // otherwise, just return the houseHoldSize
        if (houseHoldSize >= 5) {
            return "5-6";
        } else if (houseHoldSize >= 3) {
            return "3-4";
        } else {
            return houseHoldSize;
        }
    }


    async function get_data_from_api(buildingType) {
        const url = new URL(BACKEND_URL);
        const is_business = entity_is_business(entiteit);
        const persons = get_persons(houseHoldSize);
        const params = { m: "estimate", building_type: buildingType, household_size: persons, business: is_business };
        url.search = new URLSearchParams(params).toString();
        setFetchingData(true);
        setIsFetchingData(true);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
        try {
            if (response.ok) {
                const data = await response.json();
                if (!validateApiResponse(data)) {
                    console.error('Error fetching usage estimate: invalid response', response);
                    setFetchingData(false);
                    setIsFetchingData(false);
                    return false;
                }
                setFetchingData(false);
                setIsFetchingData(false);
                return data;
            } else {
                console.error('Error getting usage estimate', response);
                setFetchingData(false);
                setIsFetchingData(false);
                return;
            }
        } catch (error) {
            console.error('Error getting usage estimate', error);
            setFetchingData(false);
            setIsFetchingData(false);
            return;
        }
    }


    const handleWoningChange = async (value) => {
        setWoning(value);
        setBuildingType(value);
        const [woningIsValid, woningErrorMessage] = validateStepTwo(value);
        onValidationChange({
            isValid: woningIsValid,
            errorMessage: woningErrorMessage
        });

        if (woningIsValid) {
            if (!fetchingData) {

                let data = await get_data_from_api(value);

                if (data) {
                    setOutput(data);
                    return;
                } else {
                    setOutput(defaultOutput);
                    return;
                }
            }
        } else {
            setOutput(defaultOutput);
            return;
        }

    };

    function validateApiResponse(data) {
        // we expect the api to respond with a json object with at least the following keys:
        // {
        //     "e_single": 2000,
        //     "e_high": 500,
        //     "e_low": 500,
        //     "gas": 900
        // }
        // if the response is not valid, we return false
        // if the response is valid, we return true
        if (data.e_single && data.e_high && data.e_low && data.gas) {
            return true;
        }
        return false;
    }


    // When the component mounts, we check if chosenBuilding has a value.
    // If it doesn't, we call setBuildingType with the default value.
    useEffect(() => {
        const fetchData = async () => {
            if (!chosenBuilding) {
                setBuildingType(woning);
                setWoning(woning);

                setFetchingData(true);
                setIsFetchingData(true);

                try {
                    let data = await get_data_from_api(woning);
                    setOutput(data || defaultOutput);
                } catch (error) {
                    console.error('Error fetching estimate:', error);
                    setFetchingData(false);
                    setIsFetchingData(false);
                    setOutput(defaultOutput);
                }

                setFetchingData(false);
                setIsFetchingData(false);
            }
        };

        fetchData();
    }, []); // Empty dependency array to ensure this only runs on mount

    const woningOptions = [
        { value: 'appartement', label: 'Appartement' },
        { value: 'tussenwoning', label: 'Tussenwoning' },
        { value: 'hoekwoning', label: 'Hoekwoning' },
        { value: 'vrijstaand', label: 'Vrijstaand' },
    ];

    const woningIcons = {
        appartement: <div className="icon-as-appartement icon-size"></div>,
        tussenwoning: <div className="icon-as-tussenwoning icon-size"></div>,
        hoekwoning: <div className="icon-as-hoekwoning icon-size"></div>,
        vrijstaand: <div className="icon-as-vrijstaand icon-size"></div>,
    };


    return (
        <>
            <ComponentHeaderTitles title="In welk type huis woon je?"/>
            <FormControl component="fieldset" className='steps-rows'>
                <RadioGroup aria-label="woning" name="woning" value={woning}>
                    {woningOptions.map((option) => (
                        <Card
                            key={option.value}
                            onClick={() => handleWoningChange(option.value)}
                        >
                            <CardContent>
                                <FormControlLabel
                                    value={option.value}
                                    control={<Radio disabled={fetchingData} />} // Disable the radio button if data is being fetched
                                    label={
                                        <div className="icon-text-wrapper">
                                            {woningIcons[option.value]}
                                            <Typography variant="h6" >
                                                {option.label}
                                            </Typography>
                                        </div>
                                    }
                                />
                            </CardContent>
                        </Card>
                    ))}
                </RadioGroup>
                {fetchingData && (
                    <div className="spinner-container">
                        <CircularProgress className='data-fetch-spinner' />
                    </div>
                )}
            </FormControl>
        </>
    );
};


ParticulierVerbruikEstimaterStep1.displayName = 'ParticulierVerbruikEstimaterStep1';
export default ParticulierVerbruikEstimaterStep1;
